import { array, bool, node, object } from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { isReactComponent } from 'ravenjs/utils/component';
import Typography from 'ravenjs/lib/Typography';
import Divider from 'ravenjs/lib/Divider';
import { genID } from 'ravenjs/utils/generate';

import { DEFAULT_COLORS } from 'constants/colors';
import Acl from 'modules/acl';
import SidebarButton from './SidebarButton';
import SidebarOption from './SidebarOption';
import SidebarPaginator from './SidebarPaginator';
import SidebarDisclaimer from './SidebarDisclaimer';
import SidebarHelpText from './SidebarHelpText';

const SidebarContainer = styled.div`
    padding-top: 1rem;
    right: 0;
    background: ${DEFAULT_COLORS.WHITE};
    bottom: 0;

    @media (max-width: 1024px) {
        bottom: auto !important;
    }

    ${({ theme }) => theme.media.down('md')`
        border-left: none;
        padding: 0;
        position: relative;
        top: auto;
        width: 100%;
    `};
    ${({ theme }) => theme.media.up('md')`
        border-left: 1px solid ${DEFAULT_COLORS.LIGHTER};
        position: absolute;
        top: 0;
        width: 335px;
    `};
`;

const SidebarContent = styled.div`
    bottom: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    right: 0;

    ${({ theme }) => theme.media.up('md')`
        padding: 0 1.8rem;
        position: sticky;
        height: 100%;
        top: 80px;
    `};

    ${({ theme }) => theme.media.down('md')`
        padding: 0;
        position: relative;
        top: 0;
    `};
`;

const DividerStyled = styled(Divider)`
    margin: ${({ transparent }) => (transparent ? '0' : '20px -10px')};
    width: ${({ transparent }) => (transparent ? '0' : 'calc(100% + 20px)')};
`;

const Sidebar = ({
    options,
    buttons,
    paginator,
    checkBox,
    captionContent,
    isImpersonatingUser,
}) => {
    const renderSidebarButtons = () => {
        if (buttons) {
            return buttons.map(
                ({
                    onClick,
                    disclaimer,
                    helpText,
                    label,
                    hideDivider,
                    permissions,
                    ...btnProps
                }) => {
                    if (permissions && !Acl.check(permissions)) {
                        return null;
                    }

                    return (
                        <div key={genID()}>
                            <SidebarDisclaimer disclaimer={disclaimer} />
                            <SidebarButton
                                {...btnProps}
                                width="100%"
                                onClick={onClick}
                                label={label}
                            />
                            {checkBox}
                            <SidebarHelpText helpText={helpText} />
                            <DividerStyled color="muted" transparent={hideDivider} />
                        </div>
                    );
                }
            );
        }
        return null;
    };

    const renderSidebarPaginator = () => paginator && <SidebarPaginator config={paginator} />;

    const renderSidebarOptions = () => {
        if (options) {
            return (
                <div>
                    {options.map(option => {
                        const { Component, acl = false } = option;
                        if (isReactComponent(Component)) {
                            return <Component key={genID()} />;
                        }
                        return (
                            <React.Fragment key={genID()}>
                                <SidebarOption {...option} />
                                {acl && <DividerStyled color="muted" />}
                            </React.Fragment>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    function renderCaptionContent() {
        if (captionContent) {
            const { captionHeader, color, show } = captionContent;
            let { captionBody } = captionContent;

            if (typeof captionBody === 'string') {
                captionBody = [captionBody];
            }

            return (
                show && (
                    <>
                        <Typography color={color}>{captionHeader}</Typography>
                        {captionHeader && <DividerStyled color="muted" />}
                        {captionBody.map(caption => (
                            <Typography key={genID()} color={color}>
                                {caption}
                            </Typography>
                        ))}
                    </>
                )
            );
        }
        return null;
    }

    return (
        <SidebarContainer>
            <SidebarContent
                borderRadius="0"
                borderWidth="0"
                elevation={5}
                margin="0"
                padding={`${isImpersonatingUser ? '200px' : '140px'} 30px 10px`}
            >
                {renderSidebarOptions()}
                {renderSidebarButtons()}
                {renderCaptionContent()}
                {renderSidebarPaginator()}
            </SidebarContent>
        </SidebarContainer>
    );
};

Sidebar.propTypes = {
    options: array,
    buttons: array,
    paginator: array,
    checkBox: node,
    captionContent: object,
    isImpersonatingUser: bool,
};

Sidebar.defaultProps = {
    options: null,
    buttons: null,
    paginator: null,
    checkBox: null,
    captionContent: null,
    isImpersonatingUser: false,
};

export default Sidebar;
